import { Button, Drawer, Flex, Form, Input, Select, Space } from "antd";
import {
  ColumnTypes,
  defaultTemplate,
  DEFAULT_TEMPLATE_MAP,
  droneTableColumns,
  ITemplate,
  ITemplatePartItem
} from "../../helpers/tables";
import { observer } from "mobx-react-lite";
import ordersStorage from "../../storages/orders.storage";
import { useEffect, useState } from "react";
import { IDrone, IPossiblePart } from "@drone-parts.garphild.pro/interfaces";
import dayjs from "dayjs";
import { useQuery } from "react-query";
import { fetchMaxDroneId } from "../../queries/drones";
import dictionaryStorage from "../../storages/dictionary.storage";
import { difference } from "lodash";
import templatesStorage from "../../storages/templatesStorage";
import AddDroneDialogHeader from "./components/header";
import { useAtom } from "jotai";
import { selectedTemplateAtom, selectedTemplateIdAtom } from "../../atoms/selectedTemplateIdAtom";
import { PRODUCT_TYPE_MAP } from "../../pages/drones-page/product-types";

/* eslint-disable-next-line */
export interface AddDroneDialogProps {
  opened: boolean;
  onToggle: () => void;
  drone: IDrone;
  dronesModels: string[];
  onChange: (drone: IDrone) => void;
  onSave: (drone: IDrone) => void;
}

export function AddDroneDialog(props: AddDroneDialogProps) {
  const {
    opened,
    onToggle,
    drone,
    onChange,
    onSave,
  } = props;

  const droneTemplate = templatesStorage.templates.find((template) => template._id === drone.template);

  const [selectedTemplateId, setSelectedTemplateId] = useAtom(selectedTemplateIdAtom);
  const [selectedTemplate] = useAtom(selectedTemplateAtom);

  console.log("selectedTemplate", selectedTemplateId, selectedTemplate);

  const [template , setTemplate] = useState<ITemplate>(DEFAULT_TEMPLATE_MAP[drone.itemType || 'drone'] || defaultTemplate);
  const [searching, setSearching] = useState<Record<string, string>>({});
  const productTypeConfig = PRODUCT_TYPE_MAP[drone.itemType || 'drone'];
  const { refetch, status, data: droneConfig } = useQuery<{maxId: number, itemType: string}>(
    ['maxDroneId', drone.itemType],
    () => fetchMaxDroneId(drone.itemType || 'drone'),
    {
      enabled: false,
    }
  );

  useEffect(() => {
    templatesStorage.loadTemplates();
  }, []);

  useEffect(() => {
    if (drone?.template) {
      setSelectedTemplateId(drone.template);
    } else {
      setSelectedTemplateId(null);
    }
  }, [drone.template]);

  useEffect(() => {
    setTemplate(DEFAULT_TEMPLATE_MAP[drone.itemType || 'drone'] || defaultTemplate);
  }, [drone.itemType]);

  if (!opened) {
    return null;
  }

  const generateId = async () => {
    const result = await refetch();
    if (result.data) {
      const itemType = drone.itemType || 'drone';
      const prefix = PRODUCT_TYPE_MAP[itemType]?.prefix || itemType;
      onChange({
        ...drone,
        droneId: `${prefix} ${result.data.maxId + 1}`,
      });
    }
  }

  const updateDroneComment = (droneId: string, text: string, commentId: string) => {
    const comment = drone.comments?.find((comment) => comment._id === commentId);
    if (!comment) {
      return;
    }
    comment.text = text;
    onChange({...drone});
  }

  const updateNewDroneComment = (droneId: string, text: string) => {
    if (!drone.comments) {
      drone.comments = [];
    }
    const comment = drone.comments.find((comment) => !comment._id);
    if (comment) {
      comment.text = text;
      onChange({...drone});
      return;
    }
    drone.comments.push({
      text: text,
    });
    onChange({...drone});
  }

  const handleDronePartChange = (value: string, key: string, dronePartIndex: number) => {
      if (!value) {
        const itemIndex = dronePartIndex;
        if (itemIndex !== -1) {
          const parts = [...drone.parts];
          parts.splice(itemIndex, 1);
          drone.parts = parts;
          onChange({...drone});
        }
        return;
      }
      const item = ordersStorage
        .productsInStock
        .find((item) => item._id === value);
      if (!item) {
        return;
      }
      let quantity = 1;
      const price = item.itemPrice || 0;
      if (dronePartIndex === -1) {
        drone.parts.push({
          key: key,
          itemId: item._id?.toString() || "",
          quantity: 1,
          price: price,
          currency: item.itemCurrency || "USD",
          total: Math.round((quantity * price) * 100) / 100,
          orderId: item.orderNumber || "",
        });
      } else {
        quantity = drone.parts[dronePartIndex].quantity;
        drone.parts[dronePartIndex] = {
          key: key,
          itemId: item._id?.toString() || "",
          quantity: quantity,
          price: price,
          currency: item.itemCurrency || "USD",
          total: Math.round((quantity * price) * 100) / 100,
          orderId: item.orderNumber || "",
        };
      }
      onChange({
        ...drone,
        parts: [...drone.parts],
      });
    }

  const getDronePossiblePartsForSelected = (dronePartIndex: number, column: IPossiblePart) => {
    if (!column.partNames?.length) {
      return [];
    }
    return ordersStorage
      .productsInStock
      .filter((item) => {
        if (!item.partTypes?.length) {
          return false;
        }
        // Always include currently selected item if there is one
        if (dronePartIndex !== -1 && drone.parts[dronePartIndex]?.itemId && item._id === drone.parts[dronePartIndex]?.itemId) {
          return true;
        }

        if (!item.shortName) {
          return false;
        }

        const hasPartType = item.partTypes?.length && column.partNames?.length && column.partNames.includes(item.shortName);
        if (!hasPartType) {
          return false;
        }
        // const hasCategory = column.categories?.map((category) => category.toLowerCase()).includes(item.category?.toLowerCase() || "");
        // if (!hasCategory) {
        //   return false;
        // }

        // const hasPart = column.parts?.map((part) => part.toLowerCase()).includes(item.part?.toLowerCase() || "");
        // if (!hasPart) {
        //   return false;
        // }
        if ((item.inStock || 0) <= 0) {
          return false;
        }
        const usedItemsCount = item.used?.reduce((acc, usedItem) => acc + usedItem.quantity, 0) || 0;
        return (item.inStock || 0) > usedItemsCount;
      })
      .map((item) => {
        const name = item.shortName || `${item.itemText} / ${item.itemVariant}`;
        const inStock = item.inStock || 0;
        const orderDate = dayjs(item.orderDate);
        const usedItemsCount = item.used?.reduce((acc, usedItem) => acc + usedItem.quantity, 0) || 0;
        let label = `${orderDate?.format("YYYY-MM-DD")} - ${name} : ${inStock - usedItemsCount} (${item.itemPrice}}`;
        if (!item.received) {
          label = `Not received: ${label}`;
        }
        return { label, value: item._id }
      })
  }

  const getDronePartsForSelected = (dronePartIndex: number, column: ITemplatePartItem) => {
    if (!column.parts?.length || !column.enabled) {
      return [];
    }
    return ordersStorage
      .productsInStock
      .filter((item) => {
        if (!item.partTypes?.length) {
          return false;
        }
        // Always include currently selected item if there is one
        if (dronePartIndex !== -1 && drone.parts[dronePartIndex]?.itemId && item._id === drone.parts[dronePartIndex]?.itemId) {
          return true;
        }

        const hasPartType = item.partTypes?.length && column.parts?.length && difference(item.partTypes || [], column.parts || []).length === 0;
        if (!hasPartType) {
          return false;
        }
        // const hasCategory = column.categories?.map((category) => category.toLowerCase()).includes(item.category?.toLowerCase() || "");
        // if (!hasCategory) {
        //   return false;
        // }

        // const hasPart = column.parts?.map((part) => part.toLowerCase()).includes(item.part?.toLowerCase() || "");
        // if (!hasPart) {
        //   return false;
        // }
        if ((item.inStock || 0) <= 0) {
          return false;
        }
        const usedItemsCount = item.used?.reduce((acc, usedItem) => acc + usedItem.quantity, 0) || 0;
        return (item.inStock || 0) > usedItemsCount;
      })
      .map((item) => {
        const name = item.shortName || `${item.itemText} / ${item.itemVariant}`;
        const inStock = item.inStock || 0;
        const orderDate = dayjs(item.orderDate);
        const usedItemsCount = item.used?.reduce((acc, usedItem) => acc + usedItem.quantity, 0) || 0;
        let label = `${orderDate?.format("YYYY-MM-DD")} - ${name} : ${inStock - usedItemsCount} (${item.itemPrice}}`;
        if (!item.received) {
          label = `Not received: ${label}`;
        }
        return { label, value: item._id }
      })
  }

  const usedKeys = template.parts.map((part) => [part.key, ...(part.alternativeKeys || [])]).flat();
  const notUsedParts = drone.parts.filter((part) => !usedKeys.includes(part.key));
  const otherParts = drone
    .parts
    .filter((part) => {
      const isInTemplate = droneTemplate?.possibleParts.find((templatePart) => templatePart.groupId.toLowerCase() === part.key.toLowerCase());
      return !isInTemplate;
    });
    
  // Track which parts have been processed to handle multiple parts with the same group ID
  // This is only used for determining which value to pre-select in each row
  const processedPartIndexes = new Set();


  return (
    <Drawer
      title={`${drone._id ? 'Edit' : 'Add'} ${productTypeConfig?.label || 'Product'} ${drone.droneId} (Total: ${drone.parts.reduce((acc, item) => acc + item.total, 0).toFixed(2)})`}
      width={"75vw"}
      closable={true}
      onClose={onToggle}
      open={opened}
      extra={(
        <Space>
          <Button
            onClick={() => {
              onSave(drone);
            }}
          >
            {drone._id ? "Save" : "Add"}
          </Button>
        </Space>
      )}
    >
      <AddDroneDialogHeader
        drone={drone}
        onChange={onChange}
        generateId={generateId}
        searching={searching}
        setSearching={setSearching}
        dronesModels={props.dronesModels}
      />
      {/*</Form.Item>*/}
      <div>Parts</div>
      {droneTemplate && droneTemplate.possibleParts.map((row, rowIndex) => {
        // Find all parts with matching group ID
        const matchingPartIndexes = drone.parts
          .map((item, index) => item.key.toLowerCase() === row.groupId.toLowerCase() ? index : -1)
          .filter(index => index !== -1);
        
        // Find first unprocessed part with this group ID
        let dronePartIndex = matchingPartIndexes.find(index => !processedPartIndexes.has(index));
        
        // If found, mark it as processed
        if (dronePartIndex !== undefined) {
          processedPartIndexes.add(dronePartIndex);
        } else {
          // No unprocessed parts found with this group ID
          dronePartIndex = -1;
        }
        
        const dronePart = dronePartIndex !== -1 ? drone.parts[dronePartIndex] : undefined;
        const bgColor = "white";
        return (
          <>
            <Space>
              <Form.Item
                key={row.groupId}
                label={row.title}
                name={row.groupId}
                style={{ width: '100%', minWidth: 700, backgroundColor: bgColor }}
                labelCol={{ span: 5 }}
                layout={"horizontal"}
              >
                <Select
                  placeholder={row.title}
                  allowClear
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  onSearch={(value) => {
                    setSearching({
                      ...searching,
                      [row.groupId]: value,
                    });
                  }}
                  onChange={(value) => handleDronePartChange(value, row.groupId, dronePartIndex)}
                  value={drone.parts[dronePartIndex]?.itemId}
                  defaultValue={drone.parts[dronePartIndex]?.itemId}
                  options={getDronePossiblePartsForSelected(dronePartIndex, row)}
                  style={{ backgroundColor: bgColor }}
                />
              </Form.Item>
              <Button
                onClick={() => {
                  const part = drone.parts[dronePartIndex];

                }}
              >
                Broken
              </Button>
              {drone.parts[dronePartIndex] && row.isMultiUnit === true && (
                <Form.Item
                  key={`${row.groupId}-qty`}
                  label={"Quantity"}
                  name={`${row.groupId}-qty`}
                >
                  <Input
                    type="number"
                    step={row.groupId || 1}
                    placeholder={"Quantity"}
                    defaultValue={dronePart?.quantity}
                    value={dronePart?.quantity}
                    onChange={(e) => {
                      if (!dronePart) return;
                      const quantity = (+e.target.value) || 1;
                      const price = dronePart?.price || 1;
                      drone.parts[dronePartIndex] = {
                        ...drone.parts[dronePartIndex],
                        quantity: quantity,
                        total: Math.round((quantity * price) * 100) / 100,
                      };
                      onChange({
                        ...drone,
                        parts: [...drone.parts],
                      });

                    }}
                  />
                </Form.Item>
              )}
              {dronePart && row.isMultiUnit === true &&
                <Form.Item>{drone.parts[dronePartIndex]?.price.toFixed(2)} * {drone.parts[dronePartIndex]?.quantity} = {drone.parts[dronePartIndex]?.total}</Form.Item>}
            </Space>
          </>
        )
      })}
      {!droneTemplate && template.parts.map((column) => {
        const dronePartIndex = drone
          .parts
          .findIndex((item) => {
            if (item.key.toLowerCase() === column.key.toLowerCase() || column.parts?.includes(item.key.toLowerCase())) {
              return true;
            }
            if (column.alternativeKeys?.includes(item.key.toLowerCase())) {
              return true;
            }
            return false;
          });

        const item = ordersStorage.items.find((item) => item._id === drone.parts[dronePartIndex]?.itemId);
        let bgColor;

        if (item) {
          if (item.orderStatus === "Planning") {
            bgColor = "red";
          }
        }

        return (
          <Space>
            <Form.Item
              key={column.key}
              label={column.title}
              name={column.key}
              style={{ width: '100%', minWidth: 700, backgroundColor: bgColor }}
              labelCol={{ span: 5 }}
              layout={"horizontal"}
            >
              <Select
                placeholder={column.title}
                allowClear
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                onSearch={(value) => {
                  setSearching({
                    ...searching,
                    [column.key]: value,
                  });
                }}
                onChange={(value) => handleDronePartChange(value, column.key, dronePartIndex)}
                value={drone.parts[dronePartIndex]?.itemId}
                defaultValue={drone.parts[dronePartIndex]?.itemId}
                options={getDronePartsForSelected(dronePartIndex, column)}
                style={{ backgroundColor: bgColor }}
              />
            </Form.Item>
            <Button
              onClick={() => {
                const part = drone.parts[dronePartIndex];

              }}
            >
              Broken
            </Button>
            {drone.parts[dronePartIndex] && column.needQuantity === true && (
              <Form.Item
                key={`${column.key}-qty`}
                label={"Quantity"}
                name={`${column.key}-qty`}
              >
                <Input
                  type="number"
                  step={column.step || 1}
                  placeholder={"Quantity"}
                  defaultValue={drone.parts[dronePartIndex]?.quantity}
                  value={drone.parts[dronePartIndex]?.quantity}
                  onChange={(e) => {
                    if (!drone.parts[dronePartIndex]) return;
                    const quantity = (+e.target.value) || 1;
                    const price = drone.parts[dronePartIndex]?.price || 1;
                    drone.parts[dronePartIndex] = {
                      ...drone.parts[dronePartIndex],
                      quantity: quantity,
                      total: Math.round((quantity * price) * 100) / 100,
                    };
                    onChange({
                      ...drone,
                      parts: [...drone.parts],
                    });

                  }}
                />
              </Form.Item>
            )}
            {drone.parts[dronePartIndex] && column.needQuantity === true &&
              <Form.Item>{drone.parts[dronePartIndex]?.price.toFixed(2)} * {drone.parts[dronePartIndex]?.quantity} = {drone.parts[dronePartIndex]?.total}</Form.Item>}
          </Space>
        )
      })}
      <hr />
      <div>Old</div>
      {droneTemplate && template.parts.map((column) => {
        const isAbsentInTemplate = otherParts.some((part) => part.key.toLowerCase() === column.key.toLowerCase());
        if (!isAbsentInTemplate) {
          return null;
        }
        const dronePartIndex = drone
          .parts
          .findIndex((item) => {
            if (item.key.toLowerCase() === column.key.toLowerCase() || column.parts?.includes(item.key.toLowerCase())) {
              return true;
            }
            if (column.alternativeKeys?.includes(item.key.toLowerCase())) {
              return true;
            }
            return false;
          });

        const item = ordersStorage.items.find((item) => item._id === drone.parts[dronePartIndex]?.itemId);
        let bgColor;

        console.log("found item:", item);

        if (item) {
          if (item.orderStatus === "Planning") {
            bgColor = "red";
          }
        }

        return (
          <Space>
            <Form.Item
              key={column.key}
              label={`${column.title} / ${dronePartIndex}`}
              name={column.key}
              style={{ width: '100%', minWidth: 700, backgroundColor: bgColor }}
              labelCol={{ span: 5 }}
              layout={"horizontal"}
            >
              <Select
                placeholder={column.title}
                allowClear
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                onSearch={(value) => {
                  setSearching({
                    ...searching,
                    [column.key]: value,
                  });
                }}
                onChange={(value) => handleDronePartChange(value, column.key, dronePartIndex)}
                value={drone.parts[dronePartIndex]?.itemId}
                defaultValue={drone.parts[dronePartIndex]?.itemId}
                options={getDronePartsForSelected(dronePartIndex, column)}
                style={{ backgroundColor: bgColor }}
              />
            </Form.Item>
            <Button
              onClick={() => {
                const part = drone.parts[dronePartIndex];

              }}
            >
              Broken
            </Button>
            {drone.parts[dronePartIndex] && column.needQuantity === true && (
              <Form.Item
                key={`${column.key}-qty`}
                label={"Quantity"}
                name={`${column.key}-qty`}
              >
                <Input
                  type="number"
                  step={column.step || 1}
                  placeholder={"Quantity"}
                  defaultValue={drone.parts[dronePartIndex]?.quantity}
                  value={drone.parts[dronePartIndex]?.quantity}
                  onChange={(e) => {
                    if (!drone.parts[dronePartIndex]) return;
                    const quantity = (+e.target.value) || 1;
                    const price = drone.parts[dronePartIndex]?.price || 1;
                    drone.parts[dronePartIndex] = {
                      ...drone.parts[dronePartIndex],
                      quantity: quantity,
                      total: Math.round((quantity * price) * 100) / 100,
                    };
                    onChange({
                      ...drone,
                      parts: [...drone.parts],
                    });

                  }}
                />
              </Form.Item>
            )}
            {drone.parts[dronePartIndex] && column.needQuantity === true &&
              <Form.Item>{drone.parts[dronePartIndex]?.price.toFixed(2)} * {drone.parts[dronePartIndex]?.quantity} = {drone.parts[dronePartIndex]?.total}</Form.Item>}
          </Space>
        )
      })}
      <hr/>
      <div>Other</div>
      {notUsedParts
        .filter((part) => !['Flight controller', 'Frame', 'ESC'].includes(part.key))
        .map((dronePart) => {
          const column: ITemplatePartItem = {
            enabled: true,
            partGroup: 'other',
            type: ColumnTypes.select,
            parts: dronePart.item?.partTypes || [],
            key: dronePart.key,
            title: dronePart.key,
            needQuantity: true,
            defaultQuantity: 1,
            step: 1
          };
          const dronePartIndex = drone.parts.findIndex((item) => item.key.toLowerCase() === column.key.toLowerCase() || column.parts.includes(item.key));

          return (
            <Space>
              <Form.Item
                key={column.key}
                label={column.title}
                name={column.key}
                style={{ width: '100%', minWidth: 700 }}
                labelCol={{ span: 5 }}
                layout={"horizontal"}
              >
                <Select
                  placeholder={column.title}
                  allowClear
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  onSearch={(value) => {
                    setSearching({
                      ...searching,
                      [column.key]: value,
                    });
                  }}
                  onChange={(value) => handleDronePartChange(value, column.key, dronePartIndex)}
                  value={drone.parts[dronePartIndex]?.itemId}
                  defaultValue={drone.parts[dronePartIndex]?.itemId}
                  options={getDronePartsForSelected(dronePartIndex, column)}
                />
              </Form.Item>
              {drone.parts[dronePartIndex] && (
                <Form.Item
                  key={`${column.key}-qty`}
                  label={"Quantity"}
                  name={`${column.key}-qty`}
                >
                  <Input
                    type="number"
                    step={column.step || 1}
                    placeholder={"Quantity"}
                    defaultValue={drone.parts[dronePartIndex]?.quantity}
                    value={drone.parts[dronePartIndex]?.quantity}
                    onChange={(e) => {
                      if (!drone.parts[dronePartIndex]) return;
                      const quantity = (+e.target.value) || 1;
                      const price = drone.parts[dronePartIndex]?.price || 1;
                      drone.parts[dronePartIndex] = {
                        ...drone.parts[dronePartIndex],
                        quantity: quantity,
                        total: Math.round((quantity * price) * 100) / 100,
                      };
                      onChange({
                        ...drone,
                        parts: [...drone.parts],
                      });

                    }}
                  />
                </Form.Item>
              )}
              {drone.parts[dronePartIndex] && column.needQuantity === true &&
                <Form.Item>{drone.parts[dronePartIndex]?.price.toFixed(2)} * {drone.parts[dronePartIndex]?.quantity} = {drone.parts[dronePartIndex]?.total}</Form.Item>}
            </Space>
          )
        })}
      {dictionaryStorage.productGroups.filter(() => false).map((group) => {
        const droneParts = droneTableColumns.filter((part) => group.groupId.toLowerCase() === part.partGroup?.toLowerCase());
        return group.title && (
          <>
            <Flex
              key={group.groupId}
              style={{
                height: 0,
                border: '1px dashed black',
                marginTop: 20,
                marginBottom: 20,
                position: "relative",
              }}
              justify={"center"}
              align={"center"}
            >
              <div
                style={{
                  position: "absolute",
                  top: -12,
                  fontWeight: 'bold',
                  backgroundColor: 'white',
                  padding: "0 10px"
                }}
              >
                {group.title?.uk || group.title?.en}
              </div>
            </Flex>
            {template.parts.map((column) => {
              const dronePartIndex = drone.parts.findIndex((item) => item.key.toLowerCase() === column.key.toLowerCase() || column.parts?.includes(item.key.toLowerCase()));

              return (
                <Space>
                  <Form.Item
                    key={column.key}
                    label={column.title}
                    name={column.key}
                    style={{ width: '100%', minWidth: 700 }}
                    labelCol={{ span: 5 }}
                    layout={"horizontal"}
                  >
                    <Select
                      placeholder={column.title}
                      allowClear
                      showSearch
                      filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      onSearch={(value) => {
                        setSearching({
                          ...searching,
                          [column.key]: value,
                        });
                      }}
                      onChange={(value) => handleDronePartChange(value, column.key, dronePartIndex)}
                      value={drone.parts[dronePartIndex]?.itemId}
                      defaultValue={drone.parts[dronePartIndex]?.itemId}
                      options={getDronePartsForSelected(dronePartIndex, column)}
                    />
                  </Form.Item>
                  {drone.parts[dronePartIndex] && column.needQuantity === true && (
                    <Form.Item
                      key={`${column.key}-qty`}
                      label={"Quantity"}
                      name={`${column.key}-qty`}
                    >
                      <Input
                        type="number"
                        step={column.step || 1}
                        placeholder={"Quantity"}
                        defaultValue={drone.parts[dronePartIndex]?.quantity}
                        value={drone.parts[dronePartIndex]?.quantity}
                        onChange={(e) => {
                          if (!drone.parts[dronePartIndex]) return;
                          const quantity = (+e.target.value) || 1;
                          const price = drone.parts[dronePartIndex]?.price || 1;
                          drone.parts[dronePartIndex] = {
                            ...drone.parts[dronePartIndex],
                            quantity: quantity,
                            total: Math.round((quantity * price) * 100) / 100,
                          };
                          onChange({
                            ...drone,
                            parts: [...drone.parts],
                          });

                        }}
                      />
                    </Form.Item>
                  )}
                  {drone.parts[dronePartIndex] && column.needQuantity === true &&
                    <Form.Item>{drone.parts[dronePartIndex]?.price.toFixed(2)} * {drone.parts[dronePartIndex]?.quantity} = {drone.parts[dronePartIndex]?.total}</Form.Item>}
                </Space>
              )
            })}
          </>
        )
      })}
      {droneTableColumns
        .filter((drone) => drone.enabled !== false)
        .filter((drone) => false)
        .map((column) => {
          if (column.partGroup !== undefined) return null;
          const dronePartIndex = drone.parts.findIndex((item) => item.key.toLowerCase() === column.key.toLowerCase());
          switch (column.type) {
            case ColumnTypes.select:
              return (
                <Space>
                  <Form.Item
                    key={column.key}
                    label={column.title}
                    name={column.key}
                    style={{ width: 700 }}
                    labelCol={{ span: 5 }}
                  >
                    <Select
                      placeholder={column.title}
                      allowClear
                      showSearch
                      filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      onSearch={(value) => {
                        setSearching({
                          ...searching,
                          [column.key]: value,
                        });
                      }}
                      onChange={(value) => {
                        if (!value) {
                          const itemIndex = drone.parts.findIndex((item) => item.key === column.key);
                          if (itemIndex !== -1) {
                            const parts = [...drone.parts];
                            parts.splice(itemIndex, 1);
                            drone.parts = parts;
                            onChange({ ...drone });
                          }
                          return;
                        }
                        const item = ordersStorage
                          .productsInStock
                          .find((item) => item._id === value);
                        if (!item) {
                          return;
                        }
                        let quantity = 1;
                        const price = item.itemPrice || 0;
                        if (dronePartIndex === -1) {
                          drone.parts.push({
                            key: column.key,
                            itemId: item._id?.toString() || "",
                            quantity: 1,
                            price: price,
                            currency: item.itemCurrency || "USD",
                            total: Math.round((quantity * price) * 100) / 100,
                            orderId: item.orderNumber || "",
                          });
                        } else {
                          quantity = drone.parts[dronePartIndex].quantity;
                          drone.parts[dronePartIndex] = {
                            key: column.key,
                            itemId: item._id?.toString() || "",
                            quantity: quantity,
                            price: price,
                            currency: item.itemCurrency || "USD",
                            total: Math.round((quantity * price) * 100) / 100,
                            orderId: item.orderNumber || "",
                          };

                          onChange({ ...drone });
                        }
                      }}
                      value={drone.parts[dronePartIndex]?.itemId}
                      defaultValue={drone.parts[dronePartIndex]?.itemId}
                      options={
                        ordersStorage
                          .productsInStock
                          .filter((item) => {
                            console.log("dronePartIndex", dronePartIndex, item._id, drone.parts[dronePartIndex]?.itemId);
                            if (drone.parts[dronePartIndex]?.itemId && item._id === drone.parts[dronePartIndex]?.itemId) {
                              return true;
                            }
                            // if (!item.received) {
                            //   return false;
                            // }
                            console.log("categories", column.categories, item.category);
                            if (!column.categories?.includes(item.category || "")) {
                              return false;
                            }
                            if (!column.enabled) {
                              return false;
                            }
                            console.log("parts", column.parts, item.part);
                            const hasPart = column.parts?.map((part) => part.toLowerCase()).includes(item.part?.toLowerCase() || "");
                            if (!hasPart) {
                              return false;
                            }
                            if ((item.inStock || 0) <= 0) {
                              return false;
                            }
                            const usedItemsCount = item.used?.reduce((acc, usedItem) => acc + usedItem.quantity, 0) || 0;
                            return (item.inStock || 0) > usedItemsCount;
                          })
                          .map((item) => {
                            const name = item.shortName || `${item.itemText} / ${item.itemVariant}`;
                            const inStock = item.inStock || 0;
                            const orderDate = dayjs(item.orderDate);
                            const usedItemsCount = item.used?.reduce((acc, usedItem) => acc + usedItem.quantity, 0) || 0;
                            let label = `${orderDate?.format("YYYY-MM-DD")} - ${name} : ${inStock - usedItemsCount} (${item.itemPrice}}`;
                            if (!item.received) {
                              label = `Not received: ${label}`;
                            }
                            return { label, value: item._id }
                          })
                      }
                    />
                  </Form.Item>
                  {drone.parts[dronePartIndex] && (
                    <Form.Item
                      key={`${column.key}-qty`}
                      label={"Quantity"}
                      name={`${column.key}-qty`}
                    >
                      <Input
                        type="number"
                        step={column.step || 1}
                        placeholder={"Quantity"}
                        defaultValue={drone.parts[dronePartIndex]?.quantity}
                        value={drone.parts[dronePartIndex]?.quantity}
                        onChange={(e) => {
                          if (!drone.parts[dronePartIndex]) return;
                          const quantity = (+e.target.value) || 1;
                          const price = drone.parts[dronePartIndex]?.price || 1;
                          drone.parts[dronePartIndex] = {
                            ...drone.parts[dronePartIndex],
                            quantity: quantity,
                            total: Math.round((quantity * price) * 100) / 100,
                          };
                          onChange({
                            ...drone,
                            parts: [...drone.parts],
                          });
                        }}
                      />
                    </Form.Item>
                  )}
                  {drone.parts[dronePartIndex] &&
                    <Form.Item>{drone.parts[dronePartIndex]?.price.toFixed(2)} * {drone.parts[dronePartIndex]?.quantity} = {drone.parts[dronePartIndex]?.total}</Form.Item>}
                </Space>
              );
            default:
              return null;
          }
        })}
      {drone?.comments?.filter((comment) => !!comment._id).map((comment) => (
        <Input.TextArea
          value={comment.text}
          onChange={(e) => {
            updateDroneComment(drone._id, e.currentTarget.value, comment._id || '');
          }}
        />
      ))}
      <Form.Item
        key={'new-comment'}
        name={'new-comment'}
      >
        <Input.TextArea
          onChange={(e) => {
            updateNewDroneComment(drone._id, e.currentTarget.value);
          }}
        />
      </Form.Item>
    </Drawer>
  );
}

export default observer(AddDroneDialog);
